//  filepath: src/components/dashboard/FacilityTable.jsx
import React from "react";
import { Info } from "lucide-react";
import { fmtUSD } from "../../utils/number";

export default function FacilityTable({
  view,
  tab,
  facilityRowsMTD,
  facilityRowsYTD,
}) {
  const now = new Date();
  const thisMonthName = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  ).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const prevMonthName = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
  ).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const yearNow = now.getUTCFullYear();
  const yearPrev = yearNow - 1;

  const facilityRows =
    view === "MTD"
      ? facilityRowsMTD[tab.toLowerCase()]
      : facilityRowsYTD[tab.toLowerCase()];

  const fmtValue = (v) => {
    if (v == null) return "—";
    if (tab === "Revenue") return fmtUSD(v);
    return Number(v).toLocaleString("en-US");
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
          {view === "MTD"
            ? `Facility Performance (${tab})`
            : `Facility Performance (YTD ${tab})`}
        </h3>
        {view === "MTD" ? (
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
            <Info className="h-4 w-4" />
            <span>
              Projected total uses a simple linear projection from current MTD.
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
            <Info className="h-4 w-4" />
            <span>
              Comparing {yearNow} YTD vs {yearPrev} YTD ({tab.toLowerCase()}).
            </span>
          </div>
        )}
      </div>

      <div className="lg:hidden space-y-3">
        {facilityRows.length === 0 ? (
          <div className="rounded-xl border bg-white p-4 text-center text-gray-500">
            No data
          </div>
        ) : (
          facilityRows.map((r, i) => (
            <div key={i} className="rounded-xl border bg-white p-4 shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-blue-500 font-medium">
                  {r.name || "—"}
                </span>
                {r.rating != null && (
                  <span className="text-xs text-gray-500">
                    {Number(r.rating).toFixed(1)} ★
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-y-2 text-xs text-gray-600">
                {view === "MTD" ? (
                  <>
                    <span className="font-medium">{prevMonthName} Total</span>
                    <span className="text-right">{fmtValue(r.prevTotal)}</span>
                    <span className="font-medium">{prevMonthName} MTD</span>
                    <span className="text-right">{fmtValue(r.prevMTD)}</span>
                    <span className="font-medium">{thisMonthName} MTD</span>
                    <span className="text-right">{fmtValue(r.currMTD)}</span>
                    <span className="font-medium">
                      {thisMonthName} Projected
                    </span>
                    <span className="text-right">
                      {fmtValue(r.currProjected)}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{yearPrev} YTD</span>
                    <span className="text-right">{fmtValue(r.prevYTD)}</span>
                    <span className="font-medium">{yearNow} YTD</span>
                    <span className="text-right">{fmtValue(r.currYTD)}</span>
                    <span className="font-medium">{yearPrev} Total</span>
                    <span className="text-right">{fmtValue(r.prevTotal)}</span>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden lg:block rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              {view === "MTD" ? (
                <tr className="text-left text-gray-600">
                  <th className="px-5 py-3 font-medium">Facility</th>
                  <th className="px-5 py-3 font-medium">
                    {prevMonthName} Total
                  </th>
                  <th className="px-5 py-3 font-medium">{prevMonthName} MTD</th>
                  <th className="px-5 py-3 font-medium">{thisMonthName} MTD</th>
                  <th className="px-5 py-3 font-medium">
                    {thisMonthName} Projected Total
                  </th>
                </tr>
              ) : (
                <tr className="text-left text-gray-600">
                  <th className="px-5 py-3 font-medium">Facility</th>
                  <th className="px-5 py-3 font-medium">{yearPrev} YTD</th>
                  <th className="px-5 py-3 font-medium">{yearNow} YTD</th>
                  <th className="px-5 py-3 font-medium">{yearPrev} Total</th>
                </tr>
              )}
            </thead>
            <tbody>
              {facilityRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-6 text-center text-gray-500"
                  >
                    No data
                  </td>
                </tr>
              ) : view === "MTD" ? (
                facilityRows.map((r, i) => (
                  <tr
                    key={i}
                    className="border-t border-gray-100 hover:bg-indigo-50/40 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex flex-col">
                        <span className="text-blue-500 font-medium hover:underline cursor-default">
                          {r.name || "—"}
                        </span>
                        {r.rating != null && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            {Number(r.rating).toFixed(1)} ★
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">{fmtValue(r.prevTotal)}</td>
                    <td className="px-5 py-4">{fmtValue(r.prevMTD)}</td>
                    <td className="px-5 py-4">{fmtValue(r.currMTD)}</td>
                    <td className="px-5 py-4">{fmtValue(r.currProjected)}</td>
                  </tr>
                ))
              ) : (
                facilityRows.map((r, i) => (
                  <tr
                    key={i}
                    className="border-t border-gray-100 hover:bg-indigo-50/40 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex flex-col">
                        <span className="text-blue-500 font-medium hover:underline cursor-default">
                          {r.name || "—"}
                        </span>
                        {r.rating != null && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            {Number(r.rating).toFixed(1)} ★
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">{fmtValue(r.prevYTD)}</td>
                    <td className="px-5 py-4">{fmtValue(r.currYTD)}</td>
                    <td className="px-5 py-4">{fmtValue(r.prevTotal)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 text-xs text-gray-500 border-t bg-gray-50">
          Showing {facilityRows.length || 0}
        </div>
      </div>
    </div>
  );
}
